import { BusinessBusiness, PopularityLevel, PopularityAssessment, AudienceWarning } from "../types";
import { AudienceModeId, AUDIENCE_MODES, getAudienceModeConfig } from "../config/audienceModes";
import { analyzeBusinessProspect } from "./opportunityFinder";
import { filterActiveAndFreshTags } from "./signalFreshness";
import { refineUnconfirmedHiringSignal } from "./opportunityHelpers";
import { formatSignalLastUpdated, formatCompactDate } from "./signalNormalization";
import { calculateBusinessPopularity, getPopularityAdjustment, getBusinessExplorerPrimaryPopularityLabel } from "./popularity";
import { normalizeTags } from "./tagUtils";
import {
  buildBusinessEvidenceProfile,
  BusinessEvidenceProfile,
  EvidenceItem,
  SourceTier
} from "./evidenceEngine";

export * from "./audienceEvidenceTypes";
export * from "./audienceEvidenceSources";
export * from "./audienceEvidenceEngine";
export * from "./audienceEvidenceScoring";

export type AudienceMatchLevel = "High" | "Possible" | "Unconfirmed" | "Improbable" | "Low";

export const AUDIENCE_SIGNAL_LABELS: Record<AudienceModeId, Record<string, string>> = {
  job_seeker: {
    High: "HIGH HIRING SIGNAL",
    Possible: "PROBABLE HIRING SIGNAL",
    Unconfirmed: "UNCONFIRMED HIRING SIGNAL",
    Improbable: "IMPROBABLE HIRING SIGNAL",
    Low: "LOW HIRING SIGNAL",
  },
  recruiter: {
    High: "HIGH RECRUITER SIGNAL",
    Possible: "PROBABLE RECRUITER SIGNAL",
    Unconfirmed: "UNCONFIRMED RECRUITER SIGNAL",
    Improbable: "IMPROBABLE RECRUITER SIGNAL",
    Low: "LOW RECRUITER SIGNAL",
  },
  staffing_agency: {
    High: "HIGH STAFFING SIGNAL",
    Possible: "PROBABLE STAFFING SIGNAL",
    Unconfirmed: "UNCONFIRMED STAFFING SIGNAL",
    Improbable: "IMPROBABLE STAFFING SIGNAL",
    Low: "LOW STAFFING SIGNAL",
  },
  career_coach: {
    High: "HIGH COACHING SIGNAL",
    Possible: "PROBABLE COACHING SIGNAL",
    Unconfirmed: "UNCONFIRMED COACHING SIGNAL",
    Improbable: "IMPROBABLE COACHING SIGNAL",
    Low: "LOW COACHING SIGNAL",
  },
  workforce_organization: {
    High: "HIGH WORKFORCE SIGNAL",
    Possible: "PROBABLE WORKFORCE SIGNAL",
    Unconfirmed: "UNCONFIRMED WORKFORCE SIGNAL",
    Improbable: "IMPROBABLE WORKFORCE SIGNAL",
    Low: "LOW WORKFORCE SIGNAL",
  },
  college_career_center: {
    High: "HIGH CAMPUS SIGNAL",
    Possible: "PROBABLE CAMPUS SIGNAL",
    Unconfirmed: "UNCONFIRMED CAMPUS SIGNAL",
    Improbable: "IMPROBABLE CAMPUS SIGNAL",
    Low: "LOW CAMPUS SIGNAL",
  },
  sales_professional: {
    High: "HIGH SALES LEAD SIGNAL",
    Possible: "PROBABLE SALES LEAD SIGNAL",
    Unconfirmed: "UNCONFIRMED SALES LEAD SIGNAL",
    Improbable: "IMPROBABLE SALES LEAD SIGNAL",
    Low: "LOW SALES LEAD SIGNAL",
  },
  business_development: {
    High: "HIGH BIZDEV SIGNAL",
    Possible: "PROBABLE BIZDEV SIGNAL",
    Unconfirmed: "UNCONFIRMED BIZDEV SIGNAL",
    Improbable: "IMPROBABLE BIZDEV SIGNAL",
    Low: "LOW BIZDEV SIGNAL",
  },
  agency_freelancer: {
    High: "HIGH AGENCY LEAD SIGNAL",
    Possible: "PROBABLE AGENCY LEAD SIGNAL",
    Unconfirmed: "UNCONFIRMED AGENCY LEAD SIGNAL",
    Improbable: "IMPROBABLE AGENCY LEAD SIGNAL",
    Low: "LOW AGENCY LEAD SIGNAL",
  },
  business_explorer: {
    High: "Extremely Popular",
    Possible: "Highly Popular",
    Unconfirmed: "Moderately Popular",
    Improbable: "Limited Popularity",
    Low: "Minimal Popularity",
  },
};

export function getAudienceSignalLabel(mode: AudienceModeId, level: AudienceMatchLevel | string): string {
  if (mode === "business_explorer") {
    const str = String(level).toLowerCase();
    if (str.includes("extremely_popular") || str.includes("extremely popular")) return "Extremely Popular";
    if (str.includes("highly_popular") || str.includes("highly popular")) return "Highly Popular";
    if (str.includes("moderately_popular") || str.includes("moderately popular") || str.includes("moderate")) return "Moderately Popular";
    if (str.includes("limited_popularity") || str.includes("limited popular") || str.includes("limited") || str.includes("unpopular")) return "Limited Popularity";
    if (str.includes("minimal_popularity") || str.includes("minimal popular") || str.includes("minimal")) return "Minimal Popularity";
  }
  const modeMap = AUDIENCE_SIGNAL_LABELS[mode] || AUDIENCE_SIGNAL_LABELS.job_seeker;
  const normalizedLevel = level === "Probable" ? "Possible" : level;
  return modeMap[normalizedLevel] || modeMap.Unconfirmed || `${level.toUpperCase()} SIGNAL`;
}

export function buildUserAudienceSummary(
  mode: AudienceModeId,
  level: AudienceMatchLevel,
  profile: BusinessEvidenceProfile,
  business: BusinessBusiness
): string {
  switch (mode) {
    case "job_seeker":
      if (level === "High") {
        return "Active job listings and official recruitment confirmed for this location.";
      } else if (level === "Possible") {
        return "Official careers portal verified with potential opening signals.";
      } else if (level === "Unconfirmed") {
        return "No active job vacancies currently confirmed on official company portals.";
      } else if (level === "Improbable") {
        return "Limited public hiring activity detected on official channels.";
      } else {
        return "Low likelihood of active open job vacancies at this time.";
      }

    case "recruiter":
      if (level === "High") {
        return "Specialized roles and hard-to-fill vacancies identified for recruitment sourcing.";
      } else if (level === "Possible") {
        return "Multi-branch operations with ongoing specialized staffing needs.";
      } else if (level === "Unconfirmed") {
        return "Unconfirmed specialized role demand on public channels.";
      } else {
        return "Limited recruiter demand signals identified for this business.";
      }

    case "staffing_agency":
      if (level === "High") {
        return "Recurring shift work and high-volume workforce labor needs identified.";
      } else if (level === "Possible") {
        return "Multi-branch operations with ongoing temporary staffing demand.";
      } else {
        return "Unconfirmed staffing or high-volume workforce requirements.";
      }

    case "career_coach":
      if (level === "High" || level === "Possible") {
        return "Offers entry-level pathways, paid training, or employee development programs.";
      } else {
        return "No entry-level openings or structured career pathways confirmed.";
      }

    case "workforce_organization":
      if (level === "High" || level === "Possible") {
        return "Documented apprenticeship or community workforce development partner.";
      } else {
        return "No formal community workforce board or apprenticeship programs confirmed.";
      }

    case "college_career_center":
      if (level === "High" || level === "Possible") {
        return "Active internship, co-op, or university campus recruiting programs confirmed.";
      } else {
        return "No verified college internship or early career programs located.";
      }

    case "sales_professional":
      if (level === "High") {
        return "Missing official website or key digital features — strong digital sales prospect.";
      } else {
        return "Established digital infrastructure with low immediate web sales lead potential.";
      }

    case "business_development":
      if (level === "High" || level === "Possible") {
        return "Active geographic expansion, multi-branch growth, or supplier portal programs.";
      } else {
        return "No recent expansion announcements or active vendor portal signals confirmed.";
      }

    case "agency_freelancer":
      if (level === "High" || level === "Possible") {
        return "Identified web performance, mobile responsiveness, or local SEO optimization opportunities.";
      } else {
        return "Modern, fully optimized digital presence with limited agency lead signals.";
      }

    case "business_explorer":
    default:
      if (level === "High" || level === "Possible") {
        return "High local brand popularity, strong review volume, and established community footprint.";
      } else {
        return "Local commercial entity with verified business directory information.";
      }
  }
}

export interface RawBusinessSignals {
  hasDirectHiring: boolean;
  hasInferredHiring: boolean;
  hiringScore: number;
  hiringConfidence: number;
  hiringIndicatorCount: number;
  signalRecencyDays: number;
  distanceInMiles: number | null;
  businessCategory: string;
  hasWebsite: boolean;
  websiteStatus: "ok" | "failed" | "missing" | "no_ssl" | "not_mobile";
  hasSEOIssues: boolean;
  hasTechFindings: boolean;
  hasPhone: boolean;
  hasEmail: boolean;
  contactAvailabilityScore: number;
  hasMultipleLocations: boolean;
  hasExpansion: boolean;
  reviewsCount: number;
  rating: number;
  isNewBusiness: boolean;
  isClosed: boolean;
  isBlocked: boolean;
  isInvalid: boolean;
  isEntryLevel: boolean;
  isInternship: boolean;
}

export interface FactorBreakdown {
  name: string;
  label: string;
  rawValue: any;
  points: number;
  maxPoints: number;
  weight: number;
  contribution: number;
  description: string;
}

export interface AudienceScoreResult {
  mode: AudienceModeId;
  score: number;
  level: AudienceMatchLevel;
  levelRank: number; // 5 = High, 4 = Possible, 3 = Unconfirmed, 2 = Improbable, 1 = Low
  levelLabel: string;
  shortMatchLabel: string;
  scoreLabel: string;
  rankName: string;
  badge: string;
  userSummary: string;
  technicalSummary: string;
  summary: string;
  positiveFactors: FactorBreakdown[];
  limitingFactors: FactorBreakdown[];
  summaryTags: string[];
  reasons: string[];
  factors: FactorBreakdown[];
  contributions: Record<string, number>;
  penalties: Record<string, number>;
  rawSignals: RawBusinessSignals;

  // Signal Freshness & Verification Metadata
  first_seen_at: string;
  last_seen_at: string;
  last_checked_at: string;
  source: string;
  confidence: number; // 0.0 to 1.0
  status: "fresh" | "aging" | "stale" | "expired";
  last_recorded_date: string;
  strongestSignalName?: string;
  popularityLevel?: PopularityLevel;
  popularityAdjustment?: number;
  popularityResult?: PopularityAssessment;
  popularityWarning?: AudienceWarning | null;

  // audience_evidence_v2 metadata
  engineVersion: "audience_evidence_v2";
  evidenceProfile: BusinessEvidenceProfile;
  evidenceCoverageSummary: {
    officialSourcesCount: number;
    externalSourcesCount: number;
    reliableSourcesCount: number;
    strongEvidenceCount: number;
    unconfirmedCount: number;
    lastCheckedISO: string;
    newestSourceISO: string;
    confidenceLabel: string;
  };
}

export interface FullBusinessScoring {
  rawSignals: RawBusinessSignals;
  audienceScores: Record<AudienceModeId, AudienceScoreResult>;
  activeScore: AudienceScoreResult;
}

/**
 * Extracts raw business evidence signals.
 */
export function extractRawSignals(
  business: BusinessBusiness,
  distanceInMiles: number | null,
  keyword: string,
  prospectInput?: any
): RawBusinessSignals {
  const prospect = prospectInput || analyzeBusinessProspect(business, distanceInMiles);
  const statusLower = (business.hiringStatus || "").toLowerCase();
  const rawTags = normalizeTags(
    business.opportunityTags && business.opportunityTags.length 
      ? business.opportunityTags 
      : (prospect?.tags || [])
  );
  
  const tags = normalizeTags(filterActiveAndFreshTags(business, rawTags));
  const warnings = prospect?.categorizedSignals?.warnings || [];

  const isClosed = warnings.includes("Possibly Closed") ||
                   statusLower === "closed" ||
                   business.pipeline_stage === "Blocked" ||
                   business.pipelineStage === "Blocked" ||
                   business.is_closed === true;

  const isBlocked = business.pipeline_stage === "Blocked" || business.pipelineStage === "Blocked";
  const isInvalid = !business.name || business.name.trim().length < 2;

  const hasDirectHiring = statusLower === "hiring" ||
    tags.some(t => ["Now Hiring", "Active Job Listings", "Open Interview", "Immediate Openings"].includes(t));

  const hasInferredHiring = tags.some(t => ["Expanding Team", "Growing Workforce", "Multiple Locations Hiring", "Staffing Need"].includes(t));

  const websiteUrl = business.website;
  let websiteStatus: RawBusinessSignals["websiteStatus"] = "ok";
  if (!websiteUrl || business.website_status === "missing") {
    websiteStatus = "missing";
  } else if (business.website_status === "failed") {
    websiteStatus = "failed";
  } else if (business.website_status === "no_ssl") {
    websiteStatus = "no_ssl";
  } else if (business.has_seo_issues) {
    websiteStatus = "not_mobile";
  }

  const hasPhone = !!business.phone;
  const hasEmail = !!business.email;
  const contactAvailabilityScore = (hasPhone ? 40 : 0) + (hasEmail ? 30 : 0) + (websiteStatus === "ok" ? 30 : 0);

  const isEntryLevel = tags.some(t => t.toLowerCase().includes("entry-level") || t.toLowerCase().includes("no experience"));
  const isInternship = tags.some(t => t.toLowerCase().includes("internship") || t.toLowerCase().includes("co-op") || t.toLowerCase().includes("apprentice"));

  return {
    hasDirectHiring,
    hasInferredHiring,
    hiringScore: business.hiringScore || (hasDirectHiring ? 85 : hasInferredHiring ? 60 : 20),
    hiringConfidence: hasDirectHiring ? 0.92 : hasInferredHiring ? 0.65 : 0.30,
    hiringIndicatorCount: (hasDirectHiring ? 2 : 0) + (hasInferredHiring ? 1 : 0),
    signalRecencyDays: 5,
    distanceInMiles,
    businessCategory: business.category || "General Business",
    hasWebsite: !!websiteUrl && websiteStatus !== "missing",
    websiteStatus,
    hasSEOIssues: !!business.has_seo_issues || !!business.hasSEOIssues,
    hasTechFindings: !!business.has_tech_findings || !!business.hasTechFindings,
    hasPhone,
    hasEmail,
    contactAvailabilityScore,
    hasMultipleLocations: (business.location_count || 1) > 1,
    hasExpansion: tags.some(t => t.includes("Expansion") || t.includes("New Location")),
    reviewsCount: business.reviewsCount || 0,
    rating: business.rating || 0,
    isNewBusiness: tags.some(t => t.toLowerCase().includes("new business") || t.toLowerCase().includes("grand opening")),
    isClosed,
    isBlocked,
    isInvalid,
    isEntryLevel,
    isInternship
  };
}

/**
 * Calculates audience score using the audience_evidence_v2 architecture.
 */
export function calculateAudienceScore(
  business: BusinessBusiness,
  distanceInMiles: number | null,
  keyword: string,
  mode: AudienceModeId,
  prospectInput?: any,
  referenceNowISO: string = new Date().toISOString()
): AudienceScoreResult {
  const profile = buildBusinessEvidenceProfile(business, distanceInMiles, referenceNowISO);
  const rawSignals = extractRawSignals(business, distanceInMiles, keyword, prospectInput);

  const factors: FactorBreakdown[] = [];
  const positiveFactors: FactorBreakdown[] = [];
  const limitingFactors: FactorBreakdown[] = [];
  const contributions: Record<string, number> = {};
  const penalties: Record<string, number> = {};
  const reasons: string[] = [];

  let rawAudienceScore = 15; // baseline start
  let isOnlyGenericFactors = true;

  if (rawSignals.isClosed || rawSignals.isInvalid || rawSignals.isBlocked) {
    penalties["disqualified"] = -100;
  }

  const keywordLower = (keyword || "").toLowerCase().trim();
  const categoryLower = rawSignals.businessCategory.toLowerCase();
  const isCategoryMatch = keywordLower.length > 0 && (categoryLower.includes(keywordLower) || keywordLower.includes(categoryLower));

  let summaryTags: string[] = [];

  // Mode-specific evidence scoring calculations
  switch (mode) {
    case "job_seeker": {
      if (profile.employment.activeJobPostingsCount > 0 || profile.employment.hasNowHiringLanguage) {
        isOnlyGenericFactors = false;
        const pts = profile.employment.activeJobPostingsCount >= 3 ? 45 : 35;
        contributions["activeJobPosting"] = pts;
        reasons.push("Active Official Job Listings Confirmed");
        factors.push({
          name: "activeJobPosting",
          label: "Active Official Job Listings",
          rawValue: profile.employment.activeJobPostingsCount,
          points: pts,
          maxPoints: 45,
          weight: 1.0,
          contribution: pts,
          description: "Confirmed open positions or active recruitment announcements on official company channels"
        });
        summaryTags.push("Active Official Job Listing");
        if (profile.employment.activeJobPostingsCount >= 3) summaryTags.push("Multiple Openings");
      } else if (profile.employment.hasCareersPage) {
        isOnlyGenericFactors = false;
        contributions["careersPortal"] = 20;
        reasons.push("Official Careers Portal Located");
        factors.push({
          name: "careersPortal",
          label: "Official Careers Portal",
          rawValue: true,
          points: 20,
          maxPoints: 25,
          weight: 0.8,
          contribution: 20,
          description: "Verified official company careers section"
        });
        summaryTags.push("Careers Portal Available");
      } else {
        limitingFactors.push({
          name: "unconfirmedJobListing",
          label: "No Active Job Listings Confirmed",
          rawValue: false,
          points: -15,
          maxPoints: 0,
          weight: 1.0,
          contribution: -15,
          description: "No current active job vacancies verified on official portals"
        });
        summaryTags.push("Unconfirmed Hiring Signal");
      }

      if (distanceInMiles !== null && distanceInMiles <= 5.0) {
        contributions["proximity"] = 20;
        factors.push({
          name: "proximity",
          label: "Local Commute Distance",
          rawValue: distanceInMiles,
          points: 20,
          maxPoints: 20,
          weight: 0.8,
          contribution: 20,
          description: `Located within ${distanceInMiles.toFixed(1)} miles commute radius`
        });
      }
      break;
    }

    case "recruiter": {
      if (profile.employment.specializedSkillRolesCount > 0 || profile.employment.hardToFillVacanciesCount > 0) {
        isOnlyGenericFactors = false;
        const pts = 45;
        contributions["specializedVacancies"] = pts;
        reasons.push("Specialized Roles Open", "Hard-to-Fill Positions");
        factors.push({
          name: "specializedVacancies",
          label: "Specialized & Hard-to-Fill Roles",
          rawValue: profile.employment.specializedSkillRolesCount,
          points: pts,
          maxPoints: 45,
          weight: 1.0,
          contribution: pts,
          description: "Confirmed specialized roles or recurring hard-to-fill technical vacancies"
        });
        summaryTags.push(`${profile.employment.specializedSkillRolesCount || 2} Specialized Roles Open`);
      } else if (profile.locations.hasMultipleBranches && profile.employment.activeJobPostingsCount > 0) {
        isOnlyGenericFactors = false;
        contributions["multiLocationHiring"] = 30;
        reasons.push("Hiring Across Multiple Locations");
        factors.push({
          name: "multiLocationHiring",
          label: "Multi-Location Recruitment Need",
          rawValue: profile.locations.verifiedCount,
          points: 30,
          maxPoints: 35,
          weight: 0.9,
          contribution: 30,
          description: `Active hiring across ${profile.locations.verifiedCount} regional branch locations`
        });
        summaryTags.push(`Hiring Across ${profile.locations.verifiedCount} Locations`);
      } else {
        limitingFactors.push({
          name: "noRecruiterSignal",
          label: "Limited Recruiter Opportunity Evidence",
          rawValue: false,
          points: -20,
          maxPoints: 0,
          weight: 1.0,
          contribution: -20,
          description: "No specialized or long-standing vacancies verified"
        });
        summaryTags.push("Limited Recruiter Demand");
      }

      if (!profile.employment.hasInternalRecruitingTeam) {
        summaryTags.push("No Large Internal Recruiting Team Found");
      }
      break;
    }

    case "staffing_agency": {
      if (profile.employment.shiftWorkRolesCount > 0 || profile.employment.highTurnoverPattern) {
        isOnlyGenericFactors = false;
        const pts = 45;
        contributions["shiftWorkDemand"] = pts;
        reasons.push("Recurring Shift Roles", "High-Volume Workforce Demand");
        factors.push({
          name: "shiftWorkDemand",
          label: "Shift & High-Volume Labor Demand",
          rawValue: profile.employment.shiftWorkRolesCount,
          points: pts,
          maxPoints: 45,
          weight: 1.0,
          contribution: pts,
          description: "Confirmed shift work, warehouse/hospitality staffing demand, or recurring temporary roles"
        });
        summaryTags.push("Multiple Shift Roles", "Recurring Workforce Hiring");
      } else if (profile.locations.hasMultipleBranches && profile.employment.activeJobPostingsCount > 0) {
        isOnlyGenericFactors = false;
        contributions["scalableWorkforce"] = 25;
        summaryTags.push("Multi-Branch Workforce Demand");
      } else {
        limitingFactors.push({
          name: "noStaffingDemand",
          label: "No High-Volume Staffing Need Verified",
          rawValue: false,
          points: -20,
          maxPoints: 0,
          weight: 1.0,
          contribution: -20,
          description: "Lack of shift-based, seasonal, or temporary contract labor signals"
        });
        summaryTags.push("Unconfirmed Staffing Lead");
      }
      break;
    }

    case "career_coach": {
      if (profile.employment.entryLevelRolesCount > 0 || profile.employment.paidTraining || profile.employment.tuitionAssistance) {
        isOnlyGenericFactors = false;
        const pts = 45;
        contributions["coachingTarget"] = pts;
        reasons.push("Entry-Level Roles Available", "Paid Training & Mentorship");
        factors.push({
          name: "coachingTarget",
          label: "Career-Transition Friendly Pathways",
          rawValue: true,
          points: pts,
          maxPoints: 45,
          weight: 1.0,
          contribution: pts,
          description: "Entry-level positions, paid employee development programs, or tuition assistance offered"
        });
        if (profile.employment.entryLevelRolesCount > 0) summaryTags.push("Entry-Level Roles");
        if (profile.employment.paidTraining) summaryTags.push("Paid Training");
        summaryTags.push("No Degree Required");
      } else {
        limitingFactors.push({
          name: "unconfirmedCoachingTarget",
          label: "Unconfirmed Career Transition Target",
          rawValue: false,
          points: -20,
          maxPoints: 0,
          weight: 1.0,
          contribution: -20,
          description: "Lack of entry-level openings or structured career development programs"
        });
        summaryTags.push("Unconfirmed Coaching Target");
      }
      break;
    }

    case "workforce_organization": {
      if (profile.employment.apprenticeshipsCount > 0 || profile.employment.paidTraining || profile.employment.secondChanceHiring) {
        isOnlyGenericFactors = false;
        const pts = 45;
        contributions["workforcePathway"] = pts;
        reasons.push("Apprenticeship & Paid Training Program", "Community Hiring Partner");
        factors.push({
          name: "workforcePathway",
          label: "Accessible Community Employment Pathway",
          rawValue: true,
          points: pts,
          maxPoints: 45,
          weight: 1.0,
          contribution: pts,
          description: "Documented apprenticeship, paid training, or inclusive workforce development partnership"
        });
        if (profile.employment.apprenticeshipsCount > 0) summaryTags.push("Apprenticeship Available");
        summaryTags.push("Community Hiring Partner", "Accessible Entry-Level Roles");
      } else {
        limitingFactors.push({
          name: "unconfirmedWorkforcePathway",
          label: "Unconfirmed Workforce Partnership Pathway",
          rawValue: false,
          points: -20,
          maxPoints: 0,
          weight: 1.0,
          contribution: -20,
          description: "No formal community workforce board or apprenticeship program confirmed"
        });
        summaryTags.push("Unconfirmed Workforce Pathway");
      }
      break;
    }

    case "college_career_center": {
      if (profile.employment.internshipsCount > 0 || profile.partnerships.educationalPartnershipsCount > 0 || profile.employment.tuitionAssistance) {
        isOnlyGenericFactors = false;
        const pts = 50;
        contributions["studentOpportunity"] = pts;
        reasons.push("Summer Internship Program", "Campus Recruiting Active");
        factors.push({
          name: "studentOpportunity",
          label: "Verified Student & Early Career Program",
          rawValue: true,
          points: pts,
          maxPoints: 50,
          weight: 1.0,
          contribution: pts,
          description: "Active college internship, co-op program, campus recruitment, or tuition reimbursement"
        });
        if (profile.employment.internshipsCount > 0) summaryTags.push("Summer Internship");
        summaryTags.push("Recent Graduate Role", "Campus Recruiting");
      } else {
        limitingFactors.push({
          name: "unconfirmedStudentOpp",
          label: "Unconfirmed Student / Early Career Opportunity",
          rawValue: false,
          points: -25,
          maxPoints: 0,
          weight: 1.0,
          contribution: -25,
          description: "No verified internship, co-op, or university recruiting programs found"
        });
        summaryTags.push("Unconfirmed Student Opportunity");
      }
      break;
    }

    case "sales_professional": {
      if (!profile.website.exists || profile.website.issues.length > 0) {
        isOnlyGenericFactors = false;
        const pts = !profile.website.exists ? 50 : 35;
        contributions["digitalGap"] = pts;
        reasons.push("Digital & Modernization Need", "Missing Online Booking/Ordering");
        factors.push({
          name: "digitalGap",
          label: "Operational / Digital Infrastructure Gap",
          rawValue: true,
          points: pts,
          maxPoints: 50,
          weight: 1.0,
          contribution: pts,
          description: !profile.website.exists ? "No official website located - high web sales opportunity" : "Key digital features missing (e.g., SSL, mobile responsiveness, online booking)"
        });
        if (!profile.website.exists) summaryTags.push("Missing Website");
        else summaryTags.push("No Online Booking", "Missing Service Landing Pages");
      } else {
        limitingFactors.push({
          name: "modernDigitalPresence",
          label: "Established Digital Infrastructure",
          rawValue: true,
          points: -15,
          maxPoints: 0,
          weight: 1.0,
          contribution: -15,
          description: "Website is functional and modern with online services active"
        });
      }

      if (profile.contactability.hasPhone || profile.contactability.hasEmail) {
        summaryTags.push("Owner Contact Available");
      }
      if (profile.growth.recentExpansionAnnounced) {
        summaryTags.push("New Location Opened");
      }
      break;
    }

    case "business_development": {
      if (profile.growth.recentExpansionAnnounced || profile.partnerships.supplierPortalAvailable || profile.locations.hasMultipleBranches) {
        isOnlyGenericFactors = false;
        const pts = 45;
        contributions["expansionOpportunity"] = pts;
        reasons.push("Multi-Location Expansion", "Supplier / Vendor Portal Available");
        factors.push({
          name: "expansionOpportunity",
          label: "Strategic Expansion & Partnership Readiness",
          rawValue: true,
          points: pts,
          maxPoints: 45,
          weight: 1.0,
          contribution: pts,
          description: "Confirmed geographic expansion, multi-branch operations, or public vendor portal"
        });
        summaryTags.push("Entering New Market", "Supplier Portal Available", "Multi-Location Expansion");
      } else {
        limitingFactors.push({
          name: "unconfirmedPartnership",
          label: "Unconfirmed Strategic Partnership Readiness",
          rawValue: false,
          points: -20,
          maxPoints: 0,
          weight: 1.0,
          contribution: -20,
          description: "No recent expansion announcements or active supplier programs confirmed"
        });
        summaryTags.push("Unconfirmed Partnership Target");
      }
      break;
    }

    case "agency_freelancer": {
      if (profile.website.exists && profile.website.issues.length > 0) {
        isOnlyGenericFactors = false;
        const pts = 45;
        contributions["websiteDefects"] = pts;
        reasons.push("Website Optimization Opportunities", "SEO & Schema Gaps");
        factors.push({
          name: "websiteDefects",
          label: "Documented Web & Marketing Infrastructure Deficiencies",
          rawValue: profile.website.issues.length,
          points: pts,
          maxPoints: 45,
          weight: 1.0,
          contribution: pts,
          description: `Identified ${profile.website.issues.length} technical defects (mobile responsiveness, SSL, missing schema)`
        });
        summaryTags.push("Website Not Mobile Friendly", "Missing Local Schema", "Weak Calls to Action");
      } else if (!profile.website.exists) {
        isOnlyGenericFactors = false;
        contributions["noWebsite"] = 40;
        summaryTags.push("Missing Website");
      } else {
        limitingFactors.push({
          name: "fullyOptimizedWebsite",
          label: "Modern Fully Optimized Website",
          rawValue: true,
          points: -25,
          maxPoints: 0,
          weight: 1.0,
          contribution: -25,
          description: "Website is modern, responsive, and contains structured local business schema"
        });
        summaryTags.push("Modern Website (Low Lead Potential)");
      }
      break;
    }

    case "business_explorer": {
      isOnlyGenericFactors = false;
      const popAssessment = calculateBusinessPopularity(business);
      const popAdj = getPopularityAdjustment(popAssessment, "business_explorer");
      contributions["popularity"] = popAdj;
      reasons.push(`${popAssessment.popularity_level}`);
      factors.push({
        name: "popularity",
        label: "Brand Popularity & Consumer Traction",
        rawValue: popAssessment.popularity_score,
        points: popAdj,
        maxPoints: 35,
        weight: 1.0,
        contribution: popAdj,
        description: popAssessment.popularity_evidence
      });

      if (business.rating && business.rating >= 4.0) {
        const ratingPts = business.rating >= 4.5 ? 20 : 15;
        contributions["ratingQuality"] = ratingPts;
        factors.push({
          name: "ratingQuality",
          label: "Exceptional Review Rating",
          rawValue: business.rating,
          points: ratingPts,
          maxPoints: 20,
          weight: 1.0,
          contribution: ratingPts,
          description: `Outstanding customer review rating (${business.rating.toFixed(1)}★)`
        });
      }

      if (business.reviewsCount && business.reviewsCount >= 100) {
        const volumePts = business.reviewsCount >= 1000 ? 25 : 15;
        contributions["reviewVolume"] = volumePts;
        factors.push({
          name: "reviewVolume",
          label: "High Review Volume",
          rawValue: business.reviewsCount,
          points: volumePts,
          maxPoints: 25,
          weight: 1.0,
          contribution: volumePts,
          description: `Established local anchor with ${business.reviewsCount.toLocaleString()} verified customer reviews`
        });
      }

      summaryTags.push("High Local Popularity");
      if (profile.locations.hasMultipleBranches) summaryTags.push("Multi-Branch Network");
      break;
    }
  }

  // Enrich summaryTags to ensure 2-4 distinct, highly relevant audience tags
  if (profile.employment.hasCareersPage && !summaryTags.includes("Careers Portal Available")) {
    summaryTags.push("Careers Portal Available");
  }
  if ((profile.contactability.hasPhone || profile.contactability.hasEmail) && !summaryTags.some(t => t.includes("Contact") || t.includes("Phone"))) {
    summaryTags.push("Verified Contact Available");
  }
  if (distanceInMiles !== null && distanceInMiles <= 5.0 && !summaryTags.some(t => t.includes("Commute") || t.includes("Local"))) {
    summaryTags.push("Local Commute Radius");
  }
  if (profile.locations.hasMultipleBranches && !summaryTags.some(t => t.includes("Branch") || t.includes("Locations") || t.includes("Network"))) {
    summaryTags.push("Multi-Branch Network");
  }
  summaryTags = Array.from(new Set(summaryTags)).slice(0, 4);

  // Calculate sum of contributions & penalties
  const contribPoints = Object.values(contributions).reduce((a, b) => a + b, 0);
  const penaltyPoints = Object.values(penalties).reduce((a, b) => a + b, 0);
  const limitingPoints = limitingFactors.reduce((a, f) => a + f.points, 0);

  rawAudienceScore = Math.max(0, rawAudienceScore + contribPoints + penaltyPoints + limitingPoints);

  // Evidence Counts & Source Coverage
  let reliableSourceCount = profile.sourceCoverage.officialSourcesCount + profile.sourceCoverage.reliabilityBreakdown.tier_2_trusted;
  if (mode === "sales_professional" && !profile.website.exists) {
    reliableSourceCount = Math.max(reliableSourceCount, 2);
  }
  if (mode === "business_explorer" && (business.reviewsCount || 0) >= 50) {
    reliableSourceCount = Math.max(reliableSourceCount, 2);
  }

  const strongEvidenceCount = profile.evidenceItems.filter(e => (e.sourceTier === "tier_1_official" || e.sourceTier === "tier_2_trusted") && e.confidence >= 0.8 && e.polarity === "positive" && e.audienceModes.includes(mode)).length;
  let confirmedAudienceEvidenceCount = profile.evidenceItems.filter(e => e.audienceModes.includes(mode) && (e.status === "confirmed" || e.status === "likely")).length;
  if (mode === "sales_professional" && !profile.website.exists) {
    confirmedAudienceEvidenceCount = Math.max(confirmedAudienceEvidenceCount, 1);
  }
  if (mode === "business_explorer" && (business.reviewsCount || 0) >= 50) {
    confirmedAudienceEvidenceCount = Math.max(confirmedAudienceEvidenceCount, 1);
  }

  const contradictoryEvidenceCount = profile.evidenceItems.filter(e => e.status === "contradicted").length;

  // Strict Calibration Caps (Section 25 of Prompt Requirements)
  let finalScore = rawAudienceScore;

  if (mode === "job_seeker" && !profile.employment.activeJobPostingsCount && !profile.employment.hasNowHiringLanguage) {
    finalScore = Math.min(finalScore, 45);
  }

  if (reliableSourceCount === 0) finalScore = Math.min(finalScore, 40);
  else if (reliableSourceCount === 1) finalScore = Math.min(finalScore, 45);

  if (strongEvidenceCount === 0 && mode !== "sales_professional" && mode !== "business_explorer") finalScore = Math.min(finalScore, 55);
  if (profile.identity.identityConfidence < 0.6 && mode !== "business_explorer") finalScore = Math.min(finalScore, 59);
  if (confirmedAudienceEvidenceCount === 0) finalScore = Math.min(finalScore, 64);

  // Generic factors cap rule: If score is driven ONLY by generic factors, cap at 58
  if (isOnlyGenericFactors && mode !== "business_explorer") {
    finalScore = Math.min(finalScore, 58);
  }

  // Scores >= 90 REQUIRE multiple independent reliable sources, recent evidence, high confidence (>=0.85), confirmed audience evidence >= 2, and 0 contradictions.
  if (finalScore >= 90) {
    if (reliableSourceCount < 2 || strongEvidenceCount < 2 || profile.identity.identityConfidence < 0.85 || contradictoryEvidenceCount > 0) {
      finalScore = 88;
    }
  }

  finalScore = Math.min(100, Math.max(0, Math.round(finalScore)));

  // Hard Evidence Gates (Prompt Requirements)
  const checkedRelevantOfficialSources = profile.website.exists && (profile.employment.hasCareersPage || profile.website.pagesCrawled.length > 0);
  const hasDirectNegativeEvidence = (business as any).hasNegativeEvidence ||
    profile.evidenceItems.some(e => e.polarity === "negative" && e.status === "confirmed" && e.evidenceType !== "websiteIssue") ||
    business.hiringStatus === "Closed" ||
    ((business as any).negativeEvidenceCount && (business as any).negativeEvidenceCount > 0) ||
    (business.opportunityTags && business.opportunityTags.some(t => t.includes("Closed") || t.includes("No Vacancies") || t.includes("Expired")));

  const isSearchIncomplete = (business as any).searchCompleteness === "timed_out" ||
    (business.crawlStatus as string) === "blocked" ||
    (business as any).searchCompleteness === "blocked";

  // Base Level Mapping
  let level: AudienceMatchLevel = "Low";
  let levelRank = 1;

  if (hasDirectNegativeEvidence || rawSignals.isClosed) {
    level = "Low";
    levelRank = 1;
    finalScore = Math.min(finalScore, 15);
  } else if (isSearchIncomplete) {
    level = "Unconfirmed";
    levelRank = 3;
    finalScore = 40;
  } else if (checkedRelevantOfficialSources && !profile.employment.activeJobPostingsCount && !profile.employment.hasNowHiringLanguage && mode === "job_seeker") {
    if (profile.employment.hasCareersPage && !hasDirectNegativeEvidence) {
      level = "Unconfirmed";
      levelRank = 3;
      finalScore = 45;
    } else {
      level = "Improbable";
      levelRank = 2;
      finalScore = Math.min(finalScore, 35);
    }
  } else if (finalScore >= 75 && strongEvidenceCount >= 1 && profile.identity.identityConfidence >= 0.6) {
    level = "High";
    levelRank = 5;
  } else if (finalScore >= 60 && (strongEvidenceCount >= 1 || profile.evidenceItems.some(e => e.audienceModes.includes(mode) && e.polarity === "positive"))) {
    level = "Possible";
    levelRank = 4;
  } else if (finalScore >= 20 && checkedRelevantOfficialSources) {
    level = "Improbable";
    levelRank = 2;
  } else {
    level = "Unconfirmed";
    levelRank = 3;
    finalScore = Math.max(40, Math.min(finalScore, 55));
  }

  const modeConfig = AUDIENCE_MODES[mode] || AUDIENCE_MODES["job_seeker"];
  const popAssessment = calculateBusinessPopularity(business);
  const popAdj = getPopularityAdjustment(popAssessment, mode);
  const popLabel = getBusinessExplorerPrimaryPopularityLabel(popAssessment);

  const fullSignalLabel = mode === "business_explorer" ? popLabel : getAudienceSignalLabel(mode, level);
  const levelLabel = fullSignalLabel;
  const shortMatchLabel = fullSignalLabel;

  // Build supporting & limiting explanation string for technical diagnostics
  const supportingDesc = factors.map(f => f.label).join(", ") || "Baseline registered commercial entity";
  const limitingDesc = limitingFactors.map(f => f.label).join(", ") || "No major restrictive factors found";
  const confidenceLabel = profile.identity.identityConfidence >= 0.85 ? "High" : profile.identity.identityConfidence >= 0.6 ? "Medium" : "Low";

  const displayedDateISO = profile.audienceLastUpdatedDates[mode] || null;
  const formattedDate = formatSignalLastUpdated(displayedDateISO);

  const technicalSummary = `Evidence coverage: ${reliableSourceCount} reliable sources, ${strongEvidenceCount} confirmed signals. ` +
    `Supporting: ${supportingDesc}. Limiting: ${limitingDesc}. ` +
    `Confidence: ${confidenceLabel} (${profile.identity.identityConfidence.toFixed(2)}). ` +
    `Last updated: ${formattedDate}.`;

  const userSummary = buildUserAudienceSummary(mode, level, profile, business);

  return {
    mode,
    score: finalScore,
    level,
    levelRank,
    levelLabel,
    shortMatchLabel,
    scoreLabel: modeConfig.terminology?.scoreTerm || "Opportunity Score",
    rankName: modeConfig.label,
    badge: fullSignalLabel,
    userSummary,
    technicalSummary,
    summary: userSummary,
    positiveFactors: factors,
    limitingFactors,
    summaryTags,
    reasons,
    factors,
    contributions,
    penalties,
    rawSignals,
    first_seen_at: profile.auditsCompletedAtISO,
    last_seen_at: displayedDateISO,
    last_checked_at: profile.freshness.lastCheckedISO,
    source: reliableSourceCount > 0 ? "Official & Verified Public Registries" : "Public Business Listings",
    confidence: profile.identity.identityConfidence,
    status: "fresh",
    last_recorded_date: formattedDate,
    strongestSignalName: factors[0]?.label || "Verified Entity",
    popularityLevel: popAssessment.level as PopularityLevel,
    popularityAdjustment: popAdj,
    popularityResult: popAssessment,
    popularityWarning: null,
    engineVersion: "audience_evidence_v2",
    evidenceProfile: profile,
    evidenceCoverageSummary: {
      officialSourcesCount: profile.sourceCoverage.officialSourcesCount,
      externalSourcesCount: profile.sourceCoverage.externalSourcesCount,
      reliableSourcesCount: reliableSourceCount,
      strongEvidenceCount,
      unconfirmedCount: (profile.unconfirmedRequirements[mode] || []).length,
      lastCheckedISO: profile.freshness.lastCheckedISO,
      newestSourceISO: displayedDateISO,
      confidenceLabel
    }
  };
}

/**
 * Calculates audience scores across all audience modes for a business.
 */
export function calculateAllAudienceScores(
  business: BusinessBusiness,
  distanceInMiles: number | null,
  keyword: string,
  prospectInput?: any,
  referenceNowISO: string = new Date().toISOString()
): Record<AudienceModeId, AudienceScoreResult> {
  const modes: AudienceModeId[] = [
    "job_seeker",
    "business_explorer",
    "recruiter",
    "staffing_agency",
    "career_coach",
    "workforce_organization",
    "college_career_center",
    "sales_professional",
    "business_development",
    "agency_freelancer"
  ];

  const result = {} as Record<AudienceModeId, AudienceScoreResult>;
  for (const m of modes) {
    result[m] = calculateAudienceScore(business, distanceInMiles, keyword, m, prospectInput, referenceNowISO);
  }
  return result;
}

/**
 * Convenience function returning full business scoring structure.
 */
export function getBusinessAudienceScoring(
  business: BusinessBusiness,
  distanceInMiles: number | null,
  keyword: string,
  activeMode: AudienceModeId,
  prospectInput?: any,
  referenceNowISO: string = new Date().toISOString()
): FullBusinessScoring {
  const rawSignals = extractRawSignals(business, distanceInMiles, keyword, prospectInput);
  const audienceScores = calculateAllAudienceScores(business, distanceInMiles, keyword, prospectInput, referenceNowISO);
  const activeScore = audienceScores[activeMode] || audienceScores["job_seeker"];

  return {
    rawSignals,
    audienceScores,
    activeScore
  };
}

/**
 * Development diagnostic logger for one business score in detail.
 */
export function logAudienceRankingDiagnostics(
  business: BusinessBusiness,
  distanceInMiles: number | null,
  keyword: string,
  activeMode: AudienceModeId,
  rankPosition: number
) {
  if (typeof window === "undefined") return;
  const isDev = typeof process !== "undefined" ? process.env.NODE_ENV !== "production" : false;
  if (!isDev) return;

  const scoring = getBusinessAudienceScoring(business, distanceInMiles, keyword, activeMode);
  const active = scoring.activeScore;

  console.groupCollapsed(`🔍 [audience_evidence_v2 Diagnostic] Rank #${rankPosition}: ${business.name} (${activeMode})`);
  console.log("Canonical Entity:", active.evidenceProfile?.identity.canonicalName);
  console.log("Identity Confidence:", active.evidenceProfile?.identity.identityConfidence);
  console.log("Official Domain:", active.evidenceProfile?.identity.officialDomain);
  console.log("Pages Crawled:", active.evidenceProfile?.website.pagesCrawled);
  console.log("Final Score:", active.score);
  console.log("Match Level:", active.level);
  console.log("Summary Explanation:", active.summary);
  console.log("Summary Tags:", active.summaryTags);
  console.table(active.factors);
  console.table(active.limitingFactors);
  console.groupEnd();
}

/**
 * Development-only ranking comparison test across audience modes.
 */
export function runAudienceModeComparisonTest(
  businesses: BusinessBusiness[],
  distanceMap: Record<string, number | null>,
  keyword: string
) {
  if (typeof window === "undefined" || businesses.length < 3) return;
  const isDev = typeof process !== "undefined" ? process.env.NODE_ENV !== "production" : false;
  if (!isDev) return;

  const testModes: AudienceModeId[] = [
    "job_seeker",
    "recruiter",
    "staffing_agency",
    "sales_professional",
    "agency_freelancer"
  ];

  const resultsByMode: Record<string, Array<{ name: string; score: number; rank: number }>> = {};

  for (const m of testModes) {
    const listCopy = [...businesses];
    listCopy.sort((a, b) => {
      const distA = distanceMap[a.id] ?? null;
      const distB = distanceMap[b.id] ?? null;

      const scoreA = calculateAudienceScore(a, distA, keyword, m).score;
      const scoreB = calculateAudienceScore(b, distB, keyword, m).score;

      if (scoreA !== scoreB) return scoreB - scoreA;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });

    resultsByMode[m] = listCopy.slice(0, 10).map((b, idx) => {
      const dist = distanceMap[b.id] ?? null;
      const sc = calculateAudienceScore(b, dist, keyword, m);
      return {
        rank: idx + 1,
        name: b.name,
        score: sc.score
      };
    });
  }

  console.group("📊 [audience_evidence_v2 Ranking Comparison Test]");
  for (const m of testModes) {
    console.log(`--- ${AUDIENCE_MODES[m]?.label || m} ---`);
    console.table(resultsByMode[m]);
  }
  console.groupEnd();
}
